/**
 * Datenzugriff hinter einer Schnittstelle: die Oberfläche kennt nur dieses Interface.
 *
 *   auth:      getSession, login, logout, passwortAendern
 *   favorites: list, add, remove, update
 *   prefs:     load, save
 *   tracks:    list, get, add, update, remove
 *
 * Angemeldet gehen die Daten über PocketBase (data/pocketbase.js) und je Konto
 * getrennt; ohne Anmeldung bleibt alles wie bisher im Browser. Ohne
 * VITE_POCKETBASE gibt es gar keine Konten, nur lokale Profile.
 */
import { createPocketbase } from './pocketbase.js';
import { mitBasis } from './basis.js';

const PB_PFAD = import.meta.env?.VITE_POCKETBASE || '';
const LS_PROFILE = 'kgva.profile';
const LS_FAVORITES = 'kgva.favorites';
const LS_PREFS = 'kgva.prefs';

function read(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function write(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* Privater Modus / voller Speicher: Daten bleiben nur in dieser Sitzung */
  }
}

const scope = (profile) => (profile ? `${LS_FAVORITES}.${profile.id}` : `${LS_FAVORITES}.guest`);
const profilId = (profile) => (profile ? profile.id : 'guest');

/* ------------------------------------------------- Touren im Browser --- */

/**
 * Aufgezeichnete Touren liegen in IndexedDB, nicht in `localStorage`.
 *
 * Eine Wanderung von drei Stunden sind leicht zehntausend Punkte; in
 * `localStorage` (rund 5 MB für alles zusammen) wären nach ein paar Touren
 * auch die Favoriten verloren. IndexedDB hat Platz und speichert Objekte,
 * statt sie durch JSON zu pressen.
 *
 * Zwei Ablagen: die Merkmale einer Tour und, getrennt davon, ihre Punkte.
 * Die Liste im Panel braucht nur die Merkmale - sie soll nicht jedes Mal
 * hunderttausend Koordinaten durch den Speicher ziehen.
 */
const DB = 'kgva';
const DB_VERSION = 1;
const TOUREN = 'touren';
const PUNKTE = 'tourpunkte';

function datenbank() {
  return new Promise((ok, fehler) => {
    if (!('indexedDB' in globalThis)) { fehler(new Error('Kein Speicher für Touren vorhanden.')); return; }
    const anfrage = indexedDB.open(DB, DB_VERSION);
    anfrage.onupgradeneeded = () => {
      const d = anfrage.result;
      if (!d.objectStoreNames.contains(TOUREN)) d.createObjectStore(TOUREN, { keyPath: 'id' });
      if (!d.objectStoreNames.contains(PUNKTE)) d.createObjectStore(PUNKTE, { keyPath: 'id' });
    };
    anfrage.onsuccess = () => ok(anfrage.result);
    anfrage.onerror = () => fehler(anfrage.error);
  });
}

function lauf(store, modus, arbeit) {
  return datenbank().then((d) => new Promise((ok, fehler) => {
    const t = d.transaction(store, modus);
    const anfrage = arbeit(t.objectStore(store));
    t.oncomplete = () => ok(anfrage?.result);
    t.onerror = () => fehler(t.error);
    t.onabort = () => fehler(t.error);
  }));
}

/**
 * Sicherung der laufenden Aufzeichnung.
 *
 * Android beendet eine WebView im Hintergrund ohne Vorwarnung, und bisher war
 * dann die ganze Wanderung weg - die Punkte lagen nur im Speicher. Die Sicherung
 * liegt immer lokal, auch mit Server: unterwegs ist Netz das Letzte, worauf man
 * sich verlassen will.
 */
const SICHERUNG = '__laufend__';

export const tourSicherung = {
  schreiben: (daten) => lauf(PUNKTE, 'readwrite', (s) => s.put({ id: SICHERUNG, ...daten })),
  lesen: () => lauf(PUNKTE, 'readonly', (s) => s.get(SICHERUNG)),
  loeschen: () => lauf(PUNKTE, 'readwrite', (s) => s.delete(SICHERUNG))
};

const localTracks = {
  async list(profile) {
    const alle = (await lauf(TOUREN, 'readonly', (s) => s.getAll())) || [];
    return alle
      .filter((t) => t.profil === profilId(profile))
      .sort((a, b) => (b.begonnen || 0) - (a.begonnen || 0));
  },
  async get(profile, id) {
    const tour = await lauf(TOUREN, 'readonly', (s) => s.get(id));
    if (!tour || tour.profil !== profilId(profile)) return null;
    const p = await lauf(PUNKTE, 'readonly', (s) => s.get(id));
    return { ...tour, punkte: p?.punkte || [] };
  },
  async add(profile, tour) {
    const { punkte = [], ...merkmale } = tour;
    const eintrag = {
      id: crypto.randomUUID(),
      profil: profilId(profile),
      createdAt: new Date().toISOString(),
      ...merkmale
    };
    await lauf(TOUREN, 'readwrite', (s) => s.put(eintrag));
    await lauf(PUNKTE, 'readwrite', (s) => s.put({ id: eintrag.id, punkte }));
    return eintrag;
  },
  async update(profile, id, patch) {
    const tour = await lauf(TOUREN, 'readonly', (s) => s.get(id));
    if (!tour || tour.profil !== profilId(profile)) return null;
    const neu = { ...tour, ...patch, id, profil: tour.profil };
    await lauf(TOUREN, 'readwrite', (s) => s.put(neu));
    return neu;
  },
  async remove(profile, id) {
    await lauf(TOUREN, 'readwrite', (s) => s.delete(id));
    await lauf(PUNKTE, 'readwrite', (s) => s.delete(id));
  }
};

/* ---------------------------------------------------------------- lokal --- */

const localBackend = {
  mode: 'local',

  auth: {
    async getSession() {
      return read(LS_PROFILE, null);
    },
    async register(name) {
      return this.login(name);
    },
    async login(name) {
      const clean = String(name || '').trim();
      if (!clean) throw new Error('Bitte einen Profilnamen angeben.');
      const profile = { id: slug(clean), name: clean, local: true };
      write(LS_PROFILE, profile);
      return profile;
    },
    async logout() {
      try {
        localStorage.removeItem(LS_PROFILE);
      } catch { /* ignorieren */ }
    }
  },

  favorites: {
    async list(profile) {
      return read(scope(profile), []);
    },
    async add(profile, fav) {
      const items = read(scope(profile), []);
      const entry = { id: crypto.randomUUID(), createdAt: new Date().toISOString(), ...fav };
      items.unshift(entry);
      write(scope(profile), items);
      return entry;
    },
    async update(profile, id, patch) {
      const items = read(scope(profile), []).map((f) => (f.id === id ? { ...f, ...patch } : f));
      write(scope(profile), items);
      return items.find((f) => f.id === id);
    },
    async remove(profile, id) {
      write(scope(profile), read(scope(profile), []).filter((f) => f.id !== id));
    }
  },

  prefs: {
    async load(profile) {
      return read(`${LS_PREFS}.${profile ? profile.id : 'guest'}`, null);
    },
    async save(profile, prefs) {
      write(`${LS_PREFS}.${profile ? profile.id : 'guest'}`, prefs);
    }
  },

  tracks: localTracks
};

function slug(s) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'profil';
}

/* ---------------------------------------------------------- Konten --- */

/** Was aus der Zeit vor den Konten noch im Browser liegt - nicht die Zwischenspeicher. */
const alteSchluessel = (praefix) => {
  try {
    return Object.keys(localStorage).filter((k) => k.startsWith(`${praefix}.`));
  } catch {
    return [];
  }
};

const pb = PB_PFAD ? createPocketbase({
  basis: mitBasis(PB_PFAD).replace(/\/+$/, ''),
  lokal: {
    tracks: {
      ...localTracks,
      /** Eine Tour aus der Serverliste, deren Punkte noch nicht im Gerät liegen. */
      nurMerkmale: (profile, t) => lauf(TOUREN, 'readwrite', (st) => st.put({ ...t, profil: profile.id }))
    },
    alteFavoriten: () => alteSchluessel(LS_FAVORITES).map((k) => [k, read(k, [])]).filter(([, l]) => l.length),
    alteFavoritenSetzen: (k, liste) => {
      if (liste.length) write(k, liste);
      else { try { localStorage.removeItem(k); } catch { /* egal */ } }
    },
    /** Touren aller lokalen Profile; `pb:…` sind Zwischenspeicher der Konten. */
    alteTouren: async () => ((await lauf(TOUREN, 'readonly', (st) => st.getAll())) || [])
      .filter((t) => !String(t.profil).startsWith('pb:')),
    altePrefs: () => {
      const k = alteSchluessel(LS_PREFS)[0];
      return k ? read(k, null) : null;
    },
    altePrefsWeg: () => {
      for (const k of alteSchluessel(LS_PREFS)) {
        try { localStorage.removeItem(k); } catch { /* egal */ }
      }
    }
  }
}) : null;

/** Angemeldet zählt das Konto, sonst das Gerät. */
const quelle = (profil) => (profil?.server ? pb : localBackend);

export const backend = {
  mode: pb ? 'server' : 'local',
  auth: pb ? pb.auth : localBackend.auth,
  favorites: {
    list: (p) => quelle(p).favorites.list(p),
    add: (p, f) => quelle(p).favorites.add(p, f),
    update: (p, id, patch) => quelle(p).favorites.update(p, id, patch),
    remove: (p, id) => quelle(p).favorites.remove(p, id)
  },
  prefs: {
    load: (p) => quelle(p).prefs.load(p),
    save: (p, daten) => quelle(p).prefs.save(p, daten)
  },
  tracks: {
    list: (p) => quelle(p).tracks.list(p),
    get: (p, id) => quelle(p).tracks.get(p, id),
    add: (p, t) => quelle(p).tracks.add(p, t),
    update: (p, id, patch) => quelle(p).tracks.update(p, id, patch),
    remove: (p, id) => quelle(p).tracks.remove(p, id)
  },
  uebernehmen: (p) => (p?.server ? pb.uebernehmen(p) : Promise.resolve(null)),
  nachholen: (p) => (p?.server ? pb.nachholen(p.id) : Promise.resolve(true))
};
export const isLocalMode = !pb;
